import { useState, useEffect } from 'react';

interface CronEditorProps {
  value: string;
  onChange: (value: string) => void;
}

interface CronPreset {
  label: string;
  value: string;
}

const PRESETS: CronPreset[] = [
  { label: 'Every hour', value: '0 * * * *' },
  { label: 'Every 6 hours', value: '0 */6 * * *' },
  { label: 'Daily at midnight', value: '0 0 * * *' },
  { label: 'Daily at 3am', value: '0 3 * * *' },
  { label: 'Twice daily', value: '0 0,12 * * *' },
  { label: 'Weekly Monday 9am', value: '0 9 * * 1' },
  { label: 'Monthly 1st', value: '0 0 1 * *' },
  { label: 'Custom', value: 'custom' },
];

const DOW_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function describeCron(expr: string): string {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return 'Invalid expression';
  const [min, hour, dom, month, dow] = parts;

  try {
    const timeStr = () => {
      if (hour === '*' && min === '*') return 'every minute';
      if (hour === '*') return `minute ${min} of every hour`;
      const parseHours = (h: string) => {
        if (h.startsWith('*/')) {
          const interval = parseInt(h.slice(2));
          return `every ${interval} hours`;
        }
        if (h.includes(',')) {
          return h.split(',').map(v => formatHour(parseInt(v))).join(' and ');
        }
        return formatHour(parseInt(h));
      };
      const parseMin = (m: string) => {
        if (m === '0') return ':00';
        return `:${m.padStart(2, '0')}`;
      };
      return `${parseHours(hour)}${hour.includes('*') ? '' : parseMin(min)}`;
    };

    const domStr = dom === '*' ? null : `day ${dom}`;
    const monthStr = month === '*' ? null : parseMonthField(month);
    const dowStr = dow === '*' ? null : parseDowField(dow);

    const timePart = timeStr();

    if (domStr && monthStr) return `${timePart} on ${domStr} of ${monthStr}`;
    if (domStr) return `${timePart} on the ${domStr} of each month`;
    if (dowStr) return `${timePart} on ${dowStr}`;
    if (monthStr) return `${timePart} in ${monthStr}`;
    return `${timePart} daily`;
  } catch {
    return expr;
  }
}

function formatHour(h: number): string {
  if (h === 0) return '12:00 AM';
  if (h < 12) return `${h}:00 AM`;
  if (h === 12) return '12:00 PM';
  return `${h - 12}:00 PM`;
}

function parseMonthField(m: string): string {
  if (m.includes(',')) return m.split(',').map(v => MONTH_NAMES[parseInt(v) - 1]).join(', ');
  const idx = parseInt(m) - 1;
  return MONTH_NAMES[idx] ?? m;
}

function parseDowField(d: string): string {
  if (d.includes(',')) return d.split(',').map(v => DOW_NAMES[parseInt(v)]).join(', ');
  return DOW_NAMES[parseInt(d)] ?? d;
}

function parseCronToParts(expr: string): { min: string; hour: string; dom: string; month: string; dow: string } {
  const parts = expr.trim().split(/\s+/);
  if (parts.length === 5) {
    return { min: parts[0], hour: parts[1], dom: parts[2], month: parts[3], dow: parts[4] };
  }
  return { min: '0', hour: '*', dom: '*', month: '*', dow: '*' };
}

function detectPreset(value: string): string {
  const match = PRESETS.find(p => p.value !== 'custom' && p.value === value);
  return match ? match.value : 'custom';
}

export default function CronEditor({ value, onChange }: CronEditorProps) {
  const [selectedPreset, setSelectedPreset] = useState<string>(() => detectPreset(value));
  const [parts, setParts] = useState(() => parseCronToParts(value));

  useEffect(() => {
    const preset = detectPreset(value);
    setSelectedPreset(preset);
    if (preset === 'custom') {
      setParts(parseCronToParts(value));
    }
  }, [value]);

  const handlePresetChange = (presetValue: string) => {
    setSelectedPreset(presetValue);
    if (presetValue !== 'custom') {
      onChange(presetValue);
    } else {
      // switch to custom — keep current parts
      const current = parseCronToParts(value);
      setParts(current);
      onChange(`${current.min} ${current.hour} ${current.dom} ${current.month} ${current.dow}`);
    }
  };

  const handlePartChange = (field: keyof typeof parts, fieldValue: string) => {
    const newParts = { ...parts, [field]: fieldValue };
    setParts(newParts);
    onChange(`${newParts.min} ${newParts.hour} ${newParts.dom} ${newParts.month} ${newParts.dow}`);
  };

  const description = describeCron(value);

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Preset</label>
        <select
          value={selectedPreset}
          onChange={(e) => handlePresetChange(e.target.value)}
          className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          {PRESETS.map((preset) => (
            <option key={preset.value} value={preset.value}>
              {preset.label}
            </option>
          ))}
        </select>
      </div>

      {selectedPreset === 'custom' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Custom Expression</label>
          <div className="grid grid-cols-5 gap-2">
            {(['min', 'hour', 'dom', 'month', 'dow'] as const).map((field) => (
              <div key={field} className="space-y-1">
                <label className="block text-xs text-gray-500 text-center capitalize">
                  {field === 'dom' ? 'Day (M)' : field === 'dow' ? 'Day (W)' : field}
                </label>
                <input
                  type="text"
                  value={parts[field]}
                  onChange={(e) => handlePartChange(field, e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 text-center focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="*"
                />
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1">Fields: minute hour day-of-month month day-of-week</p>
        </div>
      )}

      <div className="flex items-center gap-2 text-sm">
        <span className="text-gray-500">Preview:</span>
        <span className="text-gray-800 font-medium">{description}</span>
        <span className="text-gray-400 font-mono text-xs">({value})</span>
      </div>
    </div>
  );
}
